import React, { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { fmtDate } from '../lib/format';
import {
  PaperPlaneTilt, Robot, ArrowsClockwise, Trash, CheckCircle, Eye, ToggleLeft, ToggleRight, Clock, Bell,
} from '@phosphor-icons/react';

export default function Telegram() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState(null);

  const load = async () => {
    const r = await api.get('/telegram/info'); setInfo(r.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const discover = async () => {
    setDiscovering(true); setStatus(null);
    try {
      const r = await api.post('/telegram/discover');
      if (r.data.added.length > 0) {
        setStatus({ type: 'ok', msg: `✓ 添加了 ${r.data.added.length} 位接收人` });
      } else if (r.data.discovered === 0) {
        setStatus({ type: 'warn', msg: '没找到新订阅。请确保已经在 Telegram 给 bot 发过 /start 消息' });
      } else {
        setStatus({ type: 'info', msg: '订阅都已经存在,没有新增' });
      }
      load();
    } catch (e) {
      setStatus({ type: 'error', msg: formatApiError(e.response?.data?.detail) });
    } finally { setDiscovering(false); }
  };

  const test = async () => {
    setTesting(true); setStatus(null);
    try {
      const r = await api.post('/telegram/test');
      setStatus({ type: 'ok', msg: `✓ 测试消息已发送 ${r.data.sent}/${r.data.total}` });
    } catch (e) {
      setStatus({ type: 'error', msg: formatApiError(e.response?.data?.detail) });
    } finally { setTesting(false); }
  };

  const sendNow = async () => {
    if (!confirm('立刻发送今天到目前的报告?')) return; // eslint-disable-line
    setSendingNow(true); setStatus(null);
    try {
      const r = await api.post('/telegram/send-now');
      setStatus({ type: 'ok', msg: `✓ 今日报告已发送 ${r.data.sent}/${r.data.total}` });
    } catch (e) {
      setStatus({ type: 'error', msg: formatApiError(e.response?.data?.detail) });
    } finally { setSendingNow(false); }
  };

  const loadPreview = async () => {
    const r = await api.get('/telegram/preview', { params: { scope: 'today' } });
    setPreview(r.data.text);
  };

  const toggleChat = async (chatId) => {
    await api.put(`/telegram/chats/${chatId}/toggle`); load();
  };

  const removeChat = async (chatId) => {
    if (!confirm('确认移除?')) return; // eslint-disable-line
    await api.delete(`/telegram/chats/${chatId}`); load();
  };

  if (loading) return <div className="card-base p-12 text-center text-slate-500">加载中…</div>;

  return (
    <div className="space-y-5 fade-in max-w-3xl" data-testid="telegram-page">
      <div>
        <h1 className="text-2xl font-semibold text-white tracking-tight flex items-center gap-2">
          <Robot size={26} weight="duotone" className="text-cyan-400" /> Telegram 每日报告
        </h1>
        <p className="text-sm text-slate-400 mt-0.5">每天 00:00 (马来西亚时间) 自动发送日报到 Telegram</p>
      </div>

      {/* Logo download helper */}
      <div className="card-base p-5 border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-transparent">
        <div className="flex items-center gap-4">
          <img
            src="/mb968-logo.png"
            alt="MB 968 Logo"
            className="w-20 h-20 rounded-xl flex-shrink-0 shadow-lg shadow-amber-900/30"
          />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white">🎨 Bot 头像 Logo</div>
            <div className="text-xs text-slate-400 mt-1 leading-relaxed">
              下载这个 logo,在 @BotFather → /setuserpic → 选 mb968group → 上传
            </div>
            <a
              href="/mb968-logo.png"
              download="mb968-logo.png"
              data-testid="download-logo"
              className="btn-primary text-xs py-2 px-3 inline-flex items-center gap-1.5 mt-3"
            >
              ⬇ 下载 Logo (PNG)
            </a>
          </div>
        </div>
      </div>

      {/* Bot status */}
      <div className="card-base p-5 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          {info.bot ? (
            <div className="w-11 h-11 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <CheckCircle size={22} weight="duotone" className="text-emerald-400" />
            </div>
          ) : (
            <div className="w-11 h-11 rounded-lg bg-rose-500/10 border border-rose-500/30 flex items-center justify-center">
              <Robot size={22} weight="duotone" className="text-rose-400" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-base font-semibold text-white">
              {info.bot ? info.bot.first_name : 'Bot 未配置'}
            </div>
            <div className="text-xs text-slate-500 font-num truncate">
              @{info.bot?.username || '-'} · ID {info.bot?.id || '-'}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 text-center pt-4 border-t border-slate-800">
          <div>
            <div className="label-tiny mb-1">📅 时区</div>
            <div className="text-sm text-white font-num">{info.timezone}</div>
          </div>
          <div>
            <div className="label-tiny mb-1">🕐 发送时间</div>
            <div className="text-sm text-white font-num">{String(info.report_hour).padStart(2, '0')}:00</div>
          </div>
          <div>
            <div className="label-tiny mb-1">👥 接收人</div>
            <div className="text-sm text-white font-num">{info.chats.length}</div>
          </div>
        </div>
        {info.last_run && (
          <div className="mt-4 pt-4 border-t border-slate-800 text-xs text-slate-500 font-num text-center">
            <Clock size={12} className="inline mr-1" />
            上次自动运行:{fmtDate(info.last_run.ran_at)} · 发送 {info.last_run.sent_count}/{info.last_run.total_chats}
          </div>
        )}
      </div>

      {/* Setup guide */}
      {info.chats.length === 0 && (
        <div className="card-base p-5 sm:p-6 border-amber-500/30 bg-amber-500/5">
          <div className="flex items-start gap-3">
            <Bell size={22} weight="duotone" className="text-amber-400 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-white mb-2">还没有接收人 — 请先订阅</div>
              <ol className="text-sm text-slate-300 space-y-1.5 list-decimal list-inside">
                <li>用手机打开 Telegram</li>
                <li>搜索 <code className="bg-slate-900 px-2 py-0.5 rounded text-cyan-300 font-num">@{info.bot?.username}</code></li>
                <li>点 <strong className="text-amber-300">START</strong> 或发送 <code className="bg-slate-900 px-1.5 rounded">/start</code></li>
                <li>回来点下面「发现新订阅」按钮</li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="card-base p-5 sm:p-6">
        <h2 className="text-base font-semibold text-white mb-3">操作</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <button onClick={discover} disabled={discovering}
                  data-testid="discover-btn"
                  className="btn-secondary text-sm py-3 inline-flex items-center justify-center gap-2">
            <ArrowsClockwise size={16} weight="duotone" />
            {discovering ? '查找中…' : '发现新订阅'}
          </button>
          <button onClick={test} disabled={testing || info.chats.length === 0}
                  data-testid="test-btn"
                  className="btn-secondary text-sm py-3 inline-flex items-center justify-center gap-2">
            <PaperPlaneTilt size={16} weight="duotone" />
            {testing ? '发送中…' : '发测试消息'}
          </button>
          <button onClick={sendNow} disabled={sendingNow || info.chats.length === 0}
                  data-testid="send-now-btn"
                  className="btn-primary text-sm py-3 inline-flex items-center justify-center gap-2">
            <PaperPlaneTilt size={16} weight="duotone" />
            {sendingNow ? '发送中…' : '立刻发今日报告'}
          </button>
        </div>
        <button onClick={loadPreview} className="btn-ghost text-xs mt-3 inline-flex items-center gap-2" data-testid="preview-btn">
          <Eye size={14} /> 预览今日报告内容
        </button>

        {status && (
          <div className={`mt-4 text-sm rounded-lg p-3 ${
            status.type === 'ok' ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-300' :
            status.type === 'warn' ? 'bg-amber-500/10 border border-amber-500/20 text-amber-300' :
            status.type === 'error' ? 'bg-rose-500/10 border border-rose-500/20 text-rose-300' :
            'bg-indigo-500/10 border border-indigo-500/20 text-indigo-300'
          }`}>
            {status.msg}
          </div>
        )}
      </div>

      {/* Chats list */}
      <div className="card-base p-5 sm:p-6">
        <h2 className="text-base font-semibold text-white mb-3">接收人列表</h2>
        {info.chats.length === 0 && (
          <div className="text-sm text-slate-500 text-center py-4">还没有接收人</div>
        )}
        <div className="space-y-2">
          {info.chats.map(c => (
            <div key={c.chat_id} className="p-3 rounded-lg bg-slate-900/40 border border-slate-800 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-white font-medium">
                  {c.first_name || c.title || `Chat ${c.chat_id}`}
                  {c.username && <span className="text-slate-500 ml-1.5">@{c.username}</span>}
                </div>
                <div className="text-xs text-slate-500 font-num">
                  Chat ID:{c.chat_id} · 类型:{c.type}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => toggleChat(c.chat_id)} className="p-2 rounded hover:bg-slate-800">
                  {c.active
                    ? <ToggleRight size={22} weight="fill" className="text-emerald-400" />
                    : <ToggleLeft size={22} weight="fill" className="text-slate-500" />}
                </button>
                <button onClick={() => removeChat(c.chat_id)} className="p-2 rounded hover:bg-rose-500/10 text-rose-400">
                  <Trash size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Preview modal */}
      {preview !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="card-base p-6 max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col">
            <h3 className="text-lg font-semibold text-white mb-3">今日报告预览</h3>
            <div className="bg-slate-900/80 rounded-lg p-4 text-xs text-slate-300 overflow-y-auto whitespace-pre-wrap font-mono">
              {renderTelegramHTML(preview)}
            </div>
            <button onClick={() => setPreview(null)} className="btn-secondary mt-4">关闭</button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Safely render Telegram-style HTML (bold/code tags only) without dangerouslySetInnerHTML.
 * Strips any unknown tags to prevent XSS.
 */
function renderTelegramHTML(text) {
  if (!text) return null;
  // Tokenize on supported tags only: <b>, </b>, <i>, </i>, <code>, </code>
  const tagRegex = /(<\/?(?:b|i|code)>)/g;
  const tokens = text.split(tagRegex);
  const stack = [];
  let key = 0;
  const out = [];
  let current = [];
  const styles = {
    b: 'font-bold text-white',
    i: 'italic text-slate-400',
    code: 'bg-slate-800 px-1 rounded text-cyan-300',
  };

  const flush = () => {
    if (current.length > 0) {
      const wrapped = stack.reduceRight((acc, tag) => (
        <span key={`s-${key++}`} className={styles[tag]}>{acc}</span>
      ), <React.Fragment key={`f-${key++}`}>{current}</React.Fragment>);
      out.push(wrapped);
      current = [];
    }
  };

  for (const tok of tokens) {
    if (!tok) continue;
    const openMatch = tok.match(/^<(b|i|code)>$/);
    const closeMatch = tok.match(/^<\/(b|i|code)>$/);
    if (openMatch) {
      flush();
      stack.push(openMatch[1]);
    } else if (closeMatch) {
      flush();
      const idx = stack.lastIndexOf(closeMatch[1]);
      if (idx !== -1) stack.splice(idx, 1);
    } else {
      current.push(tok);
    }
  }
  flush();
  return out;
}
